<template>
  <div class="access-container">
    <HomeHeader  />
    <div class="w1200" id="main_content">
      <div class="access-content">
        <div class="content-header">
          <h1>网点信息管理</h1>
         <button class="action-btn add-btn" @click="handleShowAddAccessDialog">
           <h3>  增加网点</h3>
          </button>
        </div>

        <div class="access-table-container">
          <table class="access-table">
            <thead>
              <tr>
                <th>物流公司</th>
                <th>网点名称</th>
                <th>登录方式</th>
                <th>账户操作</th>
                <th>登录信息</th>
                <th>登录信息操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in accessData" :key="item.access_id">

                <td>{{ item.company.company_name }}</td>
                <td>
                  {{ item.site || '默认网点' }}
                  <button class="action-btn rename-btn" @click="handleRenameSite(item.access_id, item.site)">
                    重命名
                  </button>
                </td>
                <td>{{ item.company.login_type_display }}</td>

                <td class="operation-column">
                  <!-- 账号密码登录方式 -->
                  <template v-if="item.company.login_type === 1 || item.company.login_type === 3">
                    <!-- 密码未设置 -->
                    <button
                      v-if="!item.password"
                      class="action-btn create-btn"
                      @click="handleCreatePassword(item.access_id, item.company.company_name)"
                    >
                      创建
                    </button>
                    <!-- 密码已设置 -->
                    <template v-else>
                      <button
                        class="action-btn edit-btn"
                        @click="handleEditPassword(item.password.id, item.access_id, item.company.company_name)"
                      >
                        修改
                      </button>
                      &nbsp;&nbsp;
                      <button
                        class="action-btn delete-btn"
                        @click="handleDeletePassword(item.password.id)"
                      >
                        删除
                      </button>
                    </template>
                  </template>
                  <!-- 二维码登录方式 -->
                  <template v-else-if="item.company.login_type === 2">
                    <span class="qrcode-tip">注意扫码账号与平台是否匹配</span>
                  </template>
                </td>
                <td>
                  <template v-if="item.access">
                    <span class="status" :class="item.access.is_valid ? 'active' : 'inactive'">
                      {{ item.access.is_valid ? '有效' : '失效' }}
                    </span>
                    &nbsp;&nbsp;
                    <br>
                    {{ item.access.refresh_time }}
                  </template>
                  <template v-else>
                    <span class="status inactive">未设置</span>
                  </template>
                </td>
                <td class="access-info-operation">
                  <button class="action-btn update-btn" @click="handleUpdateAccessInfo(item.access_id, item.company.company_name, item.company.login_type)">
                    更新
                  </button>
                  &nbsp;&nbsp;
                  <button v-if="item.access" class="action-btn verify-btn" @click="handleVerifyAccessInfo(item.access.id)">
                    验证
                  </button>
                  &nbsp;&nbsp;
                  <button class="action-btn delete-btn" @click="handleDeleteAccess(item.access_id)">
                    删除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>



  <!-- 账号密码对话框 -->
  <AccountDialog
      v-if="showAccountDialog"
      :is-edit="isEditMode"
      :access-id="currentAccessId"
      :company-name="currentCompanyName"
      :initial-data="passwordFormInitialData"
      @close="handleAccountDialogClose"
      @success="handleAccountDialogSuccess"
  />

  <!-- 更新登录信息对话框 -->
  <UpdateAccessDialog
      v-if="showUpdateAccessDialog"
      :visible="showUpdateAccessDialog"
      :login-type="updateAccessType"
      :access-id="updateAccessId"
      :company-name="updateCompanyName"
      @close="handleUpdateAccessDialogClose"
      @success="handleUpdateAccessDialogSuccess"
  />

  <!-- 增加Access对话框 -->
  <AddAccessDialog
      v-if="showAddAccessDialog"
      :visible="showAddAccessDialog"
      @close="handleAddAccessDialogClose"
      @success="handleAddAccessDialogSuccess"
  />

  <!-- 重命名网点对话框 -->
  <RenameSiteDialog
      v-if="showRenameSiteDialog"
      :visible="showRenameSiteDialog"
      :access-id="currentAccessId"
      :current-site="currentSiteName"
      @close="handleRenameSiteDialogClose"
      @success="handleRenameSiteDialogSuccess"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { postWlApi } from '@utils/api_v1.js'
import HomeHeader from '@components/HomeHeader.vue'
import AccountDialog from '@components/AccountDialog.vue'
import UpdateAccessDialog from '@components/UpdateAccessDialog.vue'
import AddAccessDialog from '@components/AddAccessDialog.vue'
import RenameSiteDialog from '@components/RenameSiteDialog.vue'

const accessData = ref([])
const loading = ref(true)
const error = ref('')
const showChangePassword = ref(false)

// 密码对话框相关状态
const showAccountDialog = ref(false)
const isEditMode = ref(false)
const currentCompanyId = ref(0)
const currentCompanyName = ref('')
const passwordFormInitialData = ref({})

// 更新登录信息对话框相关状态
const showUpdateAccessDialog = ref(false)
const updateCompanyId = ref(0)
const updateCompanyName = ref('')
const updateAccessType = ref(0)
const updateSite = ref('')
const updateAccessId = ref(0)

// 增加Access对话框相关状态
const showAddAccessDialog = ref(false)

// 重命名网点对话框相关状态
const showRenameSiteDialog = ref(false)
const currentAccessId = ref(0)
const currentSiteName = ref('')



// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// 从后端获取数据
const fetchAccessData = async () => {
  try {
    loading.value = true
    error.value = ''
    const response = await postWlApi('/company_access')

    if (response.code === 0 && response.data?.company_access) {
      // 直接使用后端返回的数据结构
      accessData.value = response.data.company_access
    } else {
      error.value = '获取数据失败: 无效的响应格式'
      ElMessage.error(error.value)
    }
  } catch (err) {
    console.error('请求失败:', err)
    error.value = '网络错误，请稍后重试'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

// 页面挂载时获取数据
onMounted(() => {
  fetchAccessData()
})

// 处理修改密码
const handleChangePassword = () => {
  showChangePassword.value = true
}

// 处理取消修改密码
const handlePasswordChangeCancel = () => {
  showChangePassword.value = false
}

// 处理修改密码成功
const handlePasswordChangeSuccess = () => {
  showChangePassword.value = false
  ElMessage.success('密码修改成功', {
    duration: 2000,
    offset: 20
  })
  // 重新获取数据
  fetchAccessData()
}

// 处理创建密码
const handleCreatePassword = (accessId, companyName) => {
  isEditMode.value = false
  currentAccessId.value = accessId
  currentCompanyName.value = companyName
  passwordFormInitialData.value = {}
  showAccountDialog.value = true
}

// 处理修改密码
const handleEditPassword = (passwordId, accessId, companyName) => {
  // 查找该密码信息
  const passwordInfo = accessData.value.find(item => item.password && item.password.id === passwordId)
  if (passwordInfo && passwordInfo.password) {
    isEditMode.value = true
    currentAccessId.value = accessId
    currentCompanyName.value = companyName
    passwordFormInitialData.value = passwordInfo.password
    showAccountDialog.value = true
  }
}

// 处理删除密码
const handleDeletePassword = async (passwordId) => {
  try {
    await ElMessageBox.confirm('确定要删除该密码吗？', '删除确认', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    // 用户确认后执行删除操作
    const response = await postWlApi('/delete_password', { password_id: passwordId })
    if (response.code === 0) {
      ElMessage.success('密码删除成功')
      // 重新获取数据
      fetchAccessData()
    } else {
      ElMessage.error(response.message || '密码删除失败')
    }
  } catch (error) {
    // 捕获取消操作的异常
    if (error !== 'cancel' && !error.message.includes('cancel')) {
      console.error('删除密码失败:', error)
      ElMessage.error('密码删除失败')
    }
  }
}

// 处理密码对话框关闭
const handleAccountDialogClose = () => {
  showAccountDialog.value = false
}

// 处理密码对话框成功
const handleAccountDialogSuccess = () => {
  showAccountDialog.value = false
  // 重新获取数据
  fetchAccessData()
}

// 处理更新登录信息
const handleUpdateAccessInfo = (accessId, companyName, loginType) => {
  updateAccessId.value = accessId
  updateCompanyName.value = companyName
  updateAccessType.value = loginType
  showUpdateAccessDialog.value = true
}

// 处理增加Access按钮点击
const handleShowAddAccessDialog = () => {
  showAddAccessDialog.value = true
}

// 处理增加Access对话框关闭
const handleAddAccessDialogClose = () => {
  showAddAccessDialog.value = false
}

// 处理增加Access对话框成功
const handleAddAccessDialogSuccess = () => {
  showAddAccessDialog.value = false
  ElMessage.success('Access创建成功')
  // 重新获取数据
  fetchAccessData()
}

// 处理重命名网点
const handleRenameSite = (accessId, currentSite) => {
  currentAccessId.value = accessId
  currentSiteName.value = currentSite || ''
  showRenameSiteDialog.value = true
}

// 处理重命名网点对话框关闭
const handleRenameSiteDialogClose = () => {
  showRenameSiteDialog.value = false
}

// 处理重命名网点对话框成功
const handleRenameSiteDialogSuccess = () => {
  showRenameSiteDialog.value = false
  ElMessage.success('网点重命名成功')
  // 重新获取数据
  fetchAccessData()
}

// 处理删除access
const handleDeleteAccess = async (accessId) => {
  try {
    await ElMessageBox.confirm('确定要删除该Access记录吗？', '删除确认', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    // 用户确认后执行删除操作
    const response = await postWlApi('/delete_access', { access_id: accessId })
    if (response.code === 0) {
      ElMessage.success('Access删除成功')
      // 重新获取数据
      fetchAccessData()
    } else {
      ElMessage.error(response.message || 'Access删除失败')
    }
  } catch (error) {
    // 捕获取消操作的异常
    if (error !== 'cancel' && !error.message.includes('cancel')) {
      console.error('删除Access失败:', error)
      ElMessage.error('Access删除失败')
    }
  }
}

// 处理更新登录信息对话框关闭
const handleUpdateAccessDialogClose = () => {
  showUpdateAccessDialog.value = false
}

// 处理更新登录信息对话框成功
const handleUpdateAccessDialogSuccess = () => {
  showUpdateAccessDialog.value = false
  ElMessage.success('登录信息更新成功')
  // 重新获取数据
  fetchAccessData()
}

// 处理验证登录信息
const handleVerifyAccessInfo = async (accessId) => {
  try {
    // 发送验证请求到后端API
    const response = await postWlApi('/validate_access', { access_id: accessId })

    if (response.code === 0) {
      // 验证成功，更新前端数据
      const accessIndex = accessData.value.findIndex(item => item.access && item.access.id === accessId)
      if (accessIndex !== -1) {
        // 更新验证状态和时间
        accessData.value[accessIndex].access.is_valid = response.data.is_valid
        accessData.value[accessIndex].access.refresh_time = response.data.check_time

        // 显示成功消息
        if (response.data.is_valid) {
          ElMessage.success(`验证成功`)
        } else {
          ElMessage.error(`验证失败`)
        }
      }
    } else {
      // 验证失败，显示错误消息
      ElMessage.error(response.message || '验证失败')
    }
  } catch (error) {
    console.error('验证登录信息失败:', error)
    ElMessage.error('网络错误，请稍后重试')
  }
}
</script>

<style scoped>
.access-container {
  width: 100vw;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
  box-sizing: border-box;
}

.access-content {
  width: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.content-header h1 {
  font-size: 24px;
  color: #303133;
  margin: 0;
  text-align: left;
}

.add-btn {
  background-color: #409eff;
  color: white;
  padding: 8px 16px;
  font-size: 14px;
}

.add-btn:hover {
  background-color: #66b1ff;
}

.rename-btn {
  background-color: #67c23a;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  margin-left: 8px;
  float: right;
}

.rename-btn:hover {
  background-color: #85ce61;
}



.access-table-container {
  overflow-x: auto;
}

.access-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.access-table th,
.access-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e4e7ed;
  vertical-align: middle;
}

.access-table th {
  background-color: #f5f7fa;
  font-weight: bold;
  color: #303133;
  font-size: 14px;
}

.access-table td {
  color: #606266;
  font-size: 14px;
}

.access-table tbody tr:hover {
  background-color: #f5f7fa;
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.status.active {
  background-color: #f0f9eb;
  color: #67c23a;
}

.status.inactive {
  background-color: #fef0f0;
  color: #f56c6c;
}

.loading {
  text-align: center;
  padding: 40px 0;
  color: #606266;
  font-size: 16px;
}

.error-message {
  text-align: center;
  padding: 40px 0;
  color: #f56c6c;
  font-size: 16px;
}

/* 操作列样式 */
.operation-column {
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

/* 操作按钮样式 */
.action-btn {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  line-height: 1.5;
  display: inline-block;
  vertical-align: middle;
}

.create-btn {
  background-color: #409eff;
  color: white;
}

.create-btn:hover {
  background-color: #66b1ff;
}

.edit-btn {
  background-color: #67c23a;
  color: white;
}

.edit-btn:hover {
  background-color: #85ce61;
}

.delete-btn {
  background-color: #f56c6c;
  color: white;
}

.delete-btn:hover {
  background-color: #f78989;
}

/* 二维码提示样式 */
.qrcode-tip {
  color: #e6a23c;
  font-size: 12px;
  padding: 4px 8px;
  background-color: #fdf6ec;
  border-radius: 4px;
  border: 1px solid #faecd8;
}

/* 登录信息操作列样式 */
.access-info-operation {
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

/* 更新按钮样式 */
.update-btn {
  background-color: #909399;
  color: white;
}

.update-btn:hover {
  background-color: #a6a9ad;
}

/* 验证按钮样式 */
.verify-btn {
  background-color: #e6a23c;
  color: white;
}

.verify-btn:hover {
  background-color: #ebb563;
}
</style>